import React from 'react';

/**
 * Utility functions for handling paginated dropdown options from Fusion Manage
 */

/**
 * Fetch paginated options from Fusion Manage filtered field options endpoint
 * @param {String} workspaceId - Workspace ID (e.g., '98', '374')
 * @param {String} viewId - View ID (default: '1')
 * @param {String} fieldName - Field name (e.g., 'DEPARTMENT_ASSIGNED')
 * @param {Object} parentValues - Parent field values for filtering
 * @param {Number} offset - Starting offset for pagination
 * @param {Number} limit - Number of items to fetch (default: 10 for initial load)
 * @returns {Promise<Object>} - Object with { options, totalCount, hasMore, offset, limit }
 */
export const fetchPaginatedOptions = async (workspaceId, viewId, fieldName, parentValues = {}, offset = 0, limit = 10) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add parent values to query params
    Object.keys(parentValues).forEach(key => {
      if (parentValues[key]) {
        queryParams.append(key, parentValues[key]);
      }
    });
    
    // Add pagination parameters
    queryParams.append('offset', String(offset));
    queryParams.append('limit', String(limit));
    
    const response = await fetch(`/api/fm/workspaces/${workspaceId}/views/${viewId}/fields/${fieldName}/options?${queryParams.toString()}`);
    
    if (!response.ok) {
      console.error(`[paginatedOptionsUtils] HTTP ${response.status} when fetching paginated options for ${fieldName}`);
      return {
        options: [],
        totalCount: 0,
        hasMore: false,
        offset,
        limit
      };
    }
    
    const data = await response.json();
    
    if (!data.success) {
      console.error(`[paginatedOptionsUtils] Failed to fetch paginated options:`, data.error);
      return {
        options: [],
        totalCount: 0,
        hasMore: false,
        offset,
        limit
      };
    }
    
    // Map options to ReactSelect format
    const options = (data.options || []).map(opt => ({
      value: opt.value || opt.id || opt.label,
      label: opt.label || opt.value
    }));
    
    return {
      options,
      totalCount: data.totalCount || options.length,
      hasMore: data.hasMore || false,
      offset: data.offset || offset,
      limit: data.limit || limit
    };
  } catch (error) {
    console.error(`[paginatedOptionsUtils] Error fetching paginated options for ${fieldName}:`, error);
    return {
      options: [],
      totalCount: 0,
      hasMore: false,
      offset,
      limit
    };
  }
};

/**
 * Custom menu renderer for ReactSelect v1 that adds a "Load More" option
 * This works with react-select v1.2.1 which uses menuRenderer prop
 */
export const createPaginatedMenuRenderer = (onLoadMore, hasMore, isLoading, loadMoreCount) => {
  return ({ focusedOption, focusOption, labelKey, options, selectValue, valueArray }) => {
    // Default menu rendering logic for react-select v1
    const menuItems = options.map((option, i) => {
      const isFocused = option === focusedOption;
      const isSelected = valueArray && valueArray.some(val => val === option);
      const optionClass = [
        'Select-option',
        isSelected && 'is-selected',
        isFocused && 'is-focused',
        option.disabled && 'is-disabled'
      ].filter(Boolean).join(' ');

      return (
        <div
          key={`option-${i}`}
          className={optionClass}
          onMouseEnter={() => focusOption(option)}
          onClick={() => selectValue(option)}
          style={{
            padding: '8px 12px',
            cursor: option.disabled ? 'default' : 'pointer'
          }}
        >
          {option[labelKey] || option.label || option.value}
        </div>
      );
    });

    return (
      <div className="Select-menu">
        {menuItems}
        {hasMore && (
          <div className="react-select-load-more" style={{
            padding: '8px 12px',
            borderTop: '1px solid #e0e0e0',
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            cursor: isLoading ? 'wait' : 'pointer'
          }}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isLoading) {
                  onLoadMore();
                }
              }}
              disabled={isLoading}
              style={{
                background: 'none',
                border: 'none',
                color: '#0066cc',
                cursor: isLoading ? 'wait' : 'pointer',
                padding: '4px 8px',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              {isLoading ? 'Loading...' : `Load More (${loadMoreCount || 0} more available)`}
            </button>
          </div>
        )}
      </div>
    );
  };
};

